import { ElementController, httpGetJSON } from 'js-common';

/**
 * Controller for getting instagram images.
 *
 * @extends {ElementController}
 */
export default class Instagram extends ElementController {
  static id = 'Instagram';

  /**
   * DOM loaded, get nodes and bind events.
   */
  constructor() {
    super();

    // Custom REST API endpoint for the image data
    this.apiEndpoint = 'wp-json/kramers/v1/instagram';

    // ID and class name for the loading text
    this.loaderName = 'instagram-loading';

    // Class name for the loading error message
    this.errorName = 'instagram-error';

    // Class name set on the container just before images are added to the DOM
    this.renderingClass = 'instagram-inserting';

    // Class name set on the container when all images have been added
    this.doneClass = 'instagram-done';

    // Transition duration when transitioning between renderingClass and
    // doneClass
    this.transitionSpeed = 200;

    this.imageGrid = document.getElementById('instagram-grid');
    // Loaded flag to prevent unnecessary work
    this.isLoaded = false;

    this.instagramItemTemplate = document.getElementById(
      'instagram-item-template',
    ).innerHTML;
  }

  /**
   * DOM and images loaded, instantiate slider.
   */
  load() {
    this.loadImages();
  }

  /**
   * Insert a loading text before the Ajax request is done.
   */
  ajaxBefore() {
    let html = '';

    html += `<p class="${this.loaderName}" id="${this.loaderName}">`;
    html += '<span>Laddar</span>';
    html += '<span class="spinner spinner--dark"></span>';
    html += '</p>';

    this.imageGrid.innerHTML = html;
  }

  /**
   * Add a class to the container, indicating that the images are being
   * inserted.
   */
  setRenderingState() {
    this.imageGrid.className = this.renderingClass;
  }

  /**
   * Insert the images based on data from the Ajax response.
   *
   * @param {object} data - Ajax response data.
   */
  insertItems(data) {
    let output = '';
    const templateHtml = this.instagramItemTemplate;
    JSON.parse(data).forEach((item) => {
      output += templateHtml
        .replace('{{href}}', item.permalink)
        .replace('{{src}}', item.mediaUrl)
        .replace('{{alt}}', item.caption)
        .replace('{{type}}', item.mediaType);
    });

    this.imageGrid.innerHTML = output;
  }

  /**
   * Remove the 'inserting class' and add a done class to the container.
   */
  setDoneState() {
    this.imageGrid.classList.remove(this.renderingClass);
    this.imageGrid.classList.add(this.doneClass);
  }

  /**
   * Ajax success callback; insert the images.
   *
   * @param {object} data - Ajax response data.
   */
  ajaxSuccess(data) {
    this.setRenderingState();

    setTimeout(() => {
      this.insertItems(data);
    }, this.transitionSpeed);

    setTimeout(() => {
      this.setDoneState();
    }, this.transitionSpeed * 2);
  }

  /**
   * Ajax failure callback; show error message.
   */
  ajaxFailure() {
    this.imageGrid.classList.remove('spinner--dark');

    let html = '';
    html += window.kramersThemeData.instagram_fallback;
    this.imageGrid.innerHTML = html;
  }

  /**
   * Run the image loading process.
   */
  async loadImages() {
    if (!this.isLoaded) {
      this.ajaxBefore();
      try {
        const response = await httpGetJSON(this.apiEndpoint);
        if (response) {
          this.ajaxSuccess(response);
        } else {
          this.ajaxFailure();
        }
      } catch (error) {
        this.ajaxFailure();
      } finally {
        this.isLoaded = true;
      }
    }
  }
}
